import { ComponentProps, useMemo } from "react";
import { Control, FieldError, FieldPath, FieldValues, Merge, useController } from "react-hook-form";
import { useIntl } from "react-intl";
import PhoneNumberInput from "react-phone-number-input";

import CircleButton from "@components/buttons/CircleButton";
import Error from "@components/fields/Error";
import styles from "@components/fields/PhoneInput.module.css";
import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import InputLabel from "@components/typography/InputLabel";

import "react-phone-number-input/style.css";

type InputActionType = ComponentProps<typeof CircleButton>;
type InputActionsType = [InputActionType, InputActionType?];

type Props<FormData extends FieldValues> = Omit<
  ComponentProps<typeof PhoneNumberInput>,
  "className" | "id" | "onBlur" | "onChange" | "value"
> & {
  actions?: InputActionsType;
  control: Control<FormData>;
  error: Merge<FieldError, (FieldError | undefined)[]> | undefined;
  labelPlacement?: "stacked";
  name: FieldPath<FormData>;
  simpleInput?: boolean;
};

const PhoneInput = <FormData extends FieldValues>({
  actions,
  control,
  error,
  label,
  labelPlacement,
  name,
  required,
  simpleInput,
  ...inputProps
}: Props<FormData>) => {
  const {
    field: { onBlur, onChange, value }
  } = useController({ control, name });
  const intl = useIntl();

  const arrayErrors = useMemo(() => {
    if (error === undefined) {
      return undefined;
    }

    if (!Array.isArray(error)) {
      return [error];
    }
    return error;
  }, [error]);

  const inputStyles = [styles.input];

  if (simpleInput) {
    inputStyles.push(styles.simpleInput);
  }

  return (
    <div className={`${styles.wrapperItem} ${simpleInput ? styles.smallWrapperItem : ""}`}>
      {label && (
        <InputLabel htmlFor={name} labelPlacement={labelPlacement}>
          {label}
          {required && <RequiredFieldIndicator />}
        </InputLabel>
      )}
      <PhoneNumberInput
        aria-describedby={error ? `${name}-instructions-or-error` : ""}
        aria-invalid={error?.message?.length ? "true" : "false"}
        aria-label={name}
        aria-required={required}
        className={inputStyles.join(" ")}
        countrySelectProps={{ styles: { backgroundColor: "red" } }}
        defaultCountry="US"
        flagComponent={flag => <div>{flag.country}</div>}
        id={name}
        international
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        placeholder={intl.formatMessage({ id: "dictionary.enterPhoneNumber" })}
        value={value}
        {...inputProps}
      />
      {!simpleInput && <div className={error ? styles.animatedBorderError : styles.animatedBorder} />}
      {actions && actions.length > 0 && (
        <div className={styles.actions}>
          {actions.map(action => {
            if (!action) return;

            return (
              <div className={styles.action} key={action.ariaLabelKey}>
                <CircleButton fill="clear" {...action} disabled={inputProps.disabled} size="medium" />
              </div>
            );
          })}
        </div>
      )}
      {arrayErrors && arrayErrors.length > 0 && (
        <div id={`${name}-instructions-or-error`}>
          {arrayErrors?.map(error => <Error error={error} key={error?.message} />)}
        </div>
      )}
    </div>
  );
};

export default PhoneInput;
